<template>
  <v-app>
    <app-error />
    <v-navigation-drawer v-if="!loading && !systemError" v-model="drawer" app>
      <v-list dense nav>
        <template v-for="(l, i) in links">
          <v-list-item v-if="l.gateResult" :key="i" :to="l.href" link>
            <v-list-item-icon>
              <v-icon>{{ l.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t(l.text) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>
    <v-app-bar app :color="appColor" dark>
      <v-app-bar-nav-icon :disabled="loading || systemError" @click.stop="drawer = !drawer" />
      <router-link :to="systemError ? { name: 'server-error' } : { name: 'home' }" class="app-title">
        <div class="d-flex align-center">
          <v-img
            alt="Assets by CarNetiX"
            class="shrink mr-2"
            contain
            src="/img/icon_white.png"
            transition="scale-transition"
            width="40"
          />
          <div class="shrinkt mt-1 hidden-sm-and-down">Assets</div>
        </div>
      </router-link>
      <v-spacer />
      <login-bar v-if="!systemError" />
    </v-app-bar>
    <v-main style="margin-bottom: 40px">
      <v-container v-if="loading" class="fill-height">
        <v-row align="center" justify="center">
          <v-progress-circular indeterminate />
        </v-row>
      </v-container>
      <template v-else>
        <nav-chips :links="links" />
        <not-found v-if="error && error.status === 404" />
        <forbidden v-else-if="error && error.status === 403" />
        <router-view v-else />
      </template>
    </v-main>
    <v-footer color="secondary" fixed padless min-height="20">
      <a href="https://www.carnetix.org" target="_blank" class="hidden-xs-only">
        <v-img src="/img/carnetix.svg" max-height="32" contain class="shrink pt-n2" />
      </a>
      <v-spacer />
      <span class="mr-3">v0.0.3</span>
      <span class="mr-3"
        >&copy; {{ new Date().getFullYear() }}
        <a href="https://www.carnetix.org">CarNetiX GmbH</a>
      </span>
    </v-footer>
    <v-layout v-if="standalone" class="standalone-controls" column>
      <v-btn class="mb-2" fab @click="back">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-btn fab @click="forward">
        <v-icon>mdi-arrow-right</v-icon>
      </v-btn>
    </v-layout>
    <update-handler />
    <toasts />
  </v-app>
</template>

<script lang="ts">
import AppError from "@/components/app/AppError.vue";
import LoginBar from "@/components/app/LoginBar.vue";
import AuthStateMixin from "@/mixins/store/AuthStateMixin";
import { CanGate, Gate } from "@/common/Gates";
import NotFound from "@/views/NotFound.vue";
import Forbidden from "@/views/Forbidden.vue";
import RootStateMixin from "@/mixins/store/RootStateMixin";
import { Modules } from "@/store/types";
import { AuthMutations } from "@/store/modules/auth/types";
import Component, { mixins } from "vue-class-component";
import { Watch } from "vue-property-decorator";
import TenantStateMixin from "@/mixins/store/TenantStateMixin";
import NavChips from "@/components/app/NavChips.vue";
import UpdateHandler from "@/components/app/UpdateHandler.vue";
import Toasts from "@/components/app/Toasts.vue";

@Component({
  components: {
    Toasts,
    UpdateHandler,
    NavChips,
    Forbidden,
    NotFound,
    LoginBar,
    AppError,
  },
  metaInfo() {
    return {
      title: this.$t("app.home") as string,
      // eslint-disable-next-line
      // @ts-ignore
      titleTemplate: this.selectedTenant
        ? // eslint-disable-next-line
          // @ts-ignore
          "%s | " + this.selectedTenant.name + " | Assets by CarNetiX"
        : "%s | Assets by CarNetiX",
    };
  },
})
export default class App extends mixins(AuthStateMixin, RootStateMixin, TenantStateMixin) {
  drawer = false;
  loading = true;
  standalone = false;
  links = [
    {
      href: { name: "home" },
      text: "nav.home",
      icon: "mdi-home",
      gateResult: true,
    },
    {
      href: { name: "about" },
      text: "nav.about",
      icon: "mdi-information-variant",
      gate: new CanGate("view", "customers"),
      gateResult: false,
    },
    {
      href: { name: "assets.index" },
      text: "nav.assets",
      icon: "mdi-tablet-cellphone",
      gate: new CanGate("viewAny", "assets"),
      gateResult: false,
    },
    {
      href: { name: "assetCategories.index" },
      text: "nav.assetCategories",
      icon: "mdi-shape",
      gate: new CanGate("viewAny", "assetCategories"),
      gateResult: false,
    },
    {
      href: { name: "locations.index" },
      text: "nav.locations",
      icon: "mdi-office-building",
      gate: new CanGate("viewAny", "locations"),
      gateResult: false,
    },
    {
      href: { name: "owners.index" },
      text: "nav.owners",
      icon: "mdi-account-convert",
      gate: new CanGate("viewAny", "owners"),
      gateResult: false,
    },
    {
      href: { name: "storagePlaces.index" },
      text: "nav.storagePlaces",
      icon: "mdi-library-shelves",
      gate: new CanGate("viewAny", "storagePlaces"),
      gateResult: false,
    },
    {
      href: { name: "makers.index" },
      text: "nav.makers",
      icon: "mdi-handshake",
      gate: new CanGate("viewAny", "makers"),
      gateResult: false,
    },
    {
      href: { name: "users.index" },
      text: "nav.users",
      icon: "mdi-account",
      gate: new CanGate("viewAny", "users"),
      gateResult: false,
    },
    {
      href: { name: "tenants.index" },
      text: "nav.tenants",
      icon: "mdi-office-building-marker",
      gate: new CanGate("viewAny", "tenants"),
      gateResult: false,
    },
  ];

  get appColor(): string {
    return this.systemError ? "error" : "secondary";
  }

  back(): void {
    window.history.back();
  }

  forward(): void {
    window.history.forward();
  }

  gateCheck(gate: CanGate | undefined): boolean {
    if (gate instanceof Gate) {
      return gate.isAllowed();
    }
    return true;
  }

  updateGateResults(): void {
    for (let i = 0; i < this.links.length; i++) {
      this.links[i].gateResult = this.gateCheck(this.links[i].gate);
    }
  }

  mounted(): void {
    this.standalone = window.matchMedia("(display-mode: standalone)").matches;
    if (this.loggedIn) {
      this.authFetchUser()
        .then(() => {
          this.tenantFetch()
            .catch((e) => {
              if (e.isSystemError) {
                this.$store.commit(Modules.AUTH + "/" + AuthMutations.SET, null);
              }
            })
            .finally(() => {
              this.loading = false;
            });
        })
        .catch((e) => {
          if (e.isSystemError) {
            this.$store.commit(Modules.AUTH + "/" + AuthMutations.SET, null);
          }
          this.loading = false;
        });
    } else {
      this.updateGateResults();
      this.loading = false;
    }
  }

  @Watch("authUser")
  onAuthUserChanged(): void {
    this.updateGateResults();
  }

  @Watch("$route")
  onRouteChanged(): void {
    if (this.error) {
      this.resetError();
    }
  }

  reloadApp(): void {
    window.location.reload();
  }
}
</script>

<style scoped lang="scss">
header a.app-title {
  font-size: 2rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.1rem;
  text-transform: uppercase;
  text-decoration: none !important;
}

footer a {
  color: #fff;
  text-decoration: none !important;
  font-weight: bold;
}

@media (display-mode: standalone) {
  .standalone-controls {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
  }
}
</style>
